import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
  OnInit
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatNativeDateModule } from '@angular/material/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { startWith } from 'rxjs';
import {
  Constraint,
  PolicyType,
  region,
  UseCaseId
} from '@htx/policies';
import { regionOptions } from '../../../shared/data/region-options.data';
import { useCaseOptions } from '../../../shared/data/use-case-options.data';
import { TYPE_META, CATEGORY_COLORS } from '../../shared/constraint-metadata';

@Component({
  selector: 'app-constraint-popover',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatChipsModule,
    MatIconModule,
    TranslocoPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './constraint-popover.component.html',
  styleUrl: './constraint-popover.component.scss'
})
export class ConstraintPopoverComponent implements OnInit {
  readonly constraint = input.required<Constraint>();
  readonly constraintChange = output<Constraint>();
  readonly closePopover = output<void>();

  readonly PolicyType = PolicyType;
  readonly regionOptions = regionOptions;
  readonly useCaseOptions = useCaseOptions;
  readonly minDate = new Date();

  readonly meta = computed(() => {
    const m = TYPE_META.get(this.constraint().type);
    return m ?? { icon: 'help', labelKey: 'Unknown', category: 'access' as const };
  });

  readonly categoryColor = computed(() =>
    CATEGORY_COLORS[this.meta().category] ?? '#8c95a6'
  );

  // ─── Form Controls ────────────────────────────────────
  readonly bpnInput = new FormControl('');
  readonly bpnChips = signal<string[]>([]);
  readonly countCtrl = new FormControl<number | null>(null, [Validators.required, Validators.min(1)]);
  readonly regionCtrl = new FormControl<region | ''>('', Validators.required);
  readonly useCaseCtrl = new FormControl<UseCaseId | ''>('', Validators.required);
  readonly endDateCtrl = new FormControl<Date | null>(null, Validators.required);

  // ─── Bridge FormControls to Signals for reactive validation ──
  private readonly countValue = toSignal(
    this.countCtrl.valueChanges.pipe(startWith(this.countCtrl.value)),
    { initialValue: this.countCtrl.value }
  );
  private readonly regionValue = toSignal(
    this.regionCtrl.valueChanges.pipe(startWith(this.regionCtrl.value)),
    { initialValue: this.regionCtrl.value }
  );
  private readonly useCaseValue = toSignal(
    this.useCaseCtrl.valueChanges.pipe(startWith(this.useCaseCtrl.value)),
    { initialValue: this.useCaseCtrl.value }
  );
  private readonly endDateValue = toSignal(
    this.endDateCtrl.valueChanges.pipe(startWith(this.endDateCtrl.value)),
    { initialValue: this.endDateCtrl.value }
  );

  readonly isValid = computed(() => {
    const c = this.constraint();
    switch (c.type) {
      case PolicyType.PARTICIPANT_RESTRICTED:
        return this.bpnChips().length > 0;
      case PolicyType.MEMBERSHIP_STATIC:
      case PolicyType.FRAMEWORK_AGREEMENT:
        return true;
      case PolicyType.USE_CASE_MEMBERSHIP:
        return !!this.useCaseValue();
      case PolicyType.TIME_PERIOD_RESTRICTED: {
        const v = this.countValue();
        return v != null && v >= 1;
      }
      case PolicyType.END_DATE:
        return !!this.endDateValue();
      case PolicyType.COMPANY_REGION_RESTRICTED:
      case PolicyType.DATACENTER_REGION_RESTRICTED:
        return !!this.regionValue();
      default:
        return true;
    }
  });

  ngOnInit(): void {
    this.initFromConstraint(this.constraint());
  }

  private initFromConstraint(c: Constraint): void {
    switch (c.type) {
      case PolicyType.PARTICIPANT_RESTRICTED:
        this.bpnChips.set(c.bpnNumbers ?? []);
        break;
      case PolicyType.TIME_PERIOD_RESTRICTED:
        this.countCtrl.setValue(c.count || null, { emitEvent: false });
        break;
      case PolicyType.USE_CASE_MEMBERSHIP:
        this.useCaseCtrl.setValue(c.useCaseId ?? '', { emitEvent: false });
        break;
      case PolicyType.END_DATE:
        this.endDateCtrl.setValue(c.endDate ?? null, { emitEvent: false });
        break;
      case PolicyType.COMPANY_REGION_RESTRICTED:
      case PolicyType.DATACENTER_REGION_RESTRICTED:
        this.regionCtrl.setValue(c.region ?? '', { emitEvent: false });
        break;
    }
  }

  // ─── BPN Chip Management ──────────────────────────────

  addBpn(): void {
    const raw = this.bpnInput.value?.trim().replace(/\s/g, '') ?? '';
    if (!raw) return;
    const current = this.bpnChips();
    if (!current.includes(raw)) {
      this.bpnChips.set([...current, raw]);
    }
    this.bpnInput.setValue('');
  }

  removeBpn(bpn: string): void {
    this.bpnChips.set(this.bpnChips().filter(b => b !== bpn));
  }

  onBpnKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === 'Tab' || event.key === ',') {
      event.preventDefault();
      this.addBpn();
    }
  }

  // ─── Apply / Cancel ───────────────────────────────────

  apply(): void {
    const result = this.buildConstraint();
    if (result) {
      this.constraintChange.emit(result);
    }
    this.closePopover.emit();
  }

  cancel(): void {
    this.closePopover.emit();
  }

  private buildConstraint(): Constraint | null {
    const type = this.constraint().type;
    switch (type) {
      case PolicyType.PARTICIPANT_RESTRICTED:
        return { type, bpnNumbers: this.bpnChips() };
      case PolicyType.MEMBERSHIP_STATIC:
      case PolicyType.FRAMEWORK_AGREEMENT:
        return { type };
      case PolicyType.USE_CASE_MEMBERSHIP:
        return this.useCaseCtrl.value
          ? { type, useCaseId: this.useCaseCtrl.value as UseCaseId }
          : null;
      case PolicyType.TIME_PERIOD_RESTRICTED:
        return this.countCtrl.value != null
          ? { type, count: this.countCtrl.value }
          : null;
      case PolicyType.END_DATE:
        return this.endDateCtrl.value
          ? { type, endDate: this.endDateCtrl.value }
          : null;
      case PolicyType.COMPANY_REGION_RESTRICTED:
        return this.regionCtrl.value
          ? { type, region: this.regionCtrl.value as region }
          : null;
      case PolicyType.DATACENTER_REGION_RESTRICTED:
        return this.regionCtrl.value
          ? { type, region: this.regionCtrl.value as region }
          : null;
      default:
        return null;
    }
  }
}
